import {
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Spin,
  Typography,
  Space,
} from "antd";
import React, { useContext, useEffect, useState } from "react";
import { eResultCode } from "../../../utils/enum";
import DateUtils from "../../../utils/date";
import { TCrateScanResult } from "../../../types";
import { TContext, TResponseModel } from "../../../types/config";
import {
  GetScannedCrateList,
  GetScannedProductList,
} from "../../../utils/api.constant";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  CloseOutlined,
  BarcodeOutlined,
  InboxOutlined,
  CalendarOutlined,
  BoxPlotOutlined,
} from "@ant-design/icons";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import FormLayout, {
  FormContent,
  FormHeader,
} from "../../../components/form/form";
import { UserContext } from "../../../context";
const { Title } = Typography;

type TProps = {
  onCloseDrawer: () => void;
};

function CrateScannedHistory(props: TProps) {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [prevScannedResult, setPrevScannedResult] = useState<
    TCrateScanResult[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const context: TContext = useContext(UserContext);

  useEffect(() => {
    fetchPrevScanData();
  }, []);

  const fetchPrevScanData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetScannedCrateList,
        requestPayload
      );
      const { dataResponse, data } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setPrevScannedResult(data);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout>
      <FormHeader>
        <Row
          // gutter={16}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Col>
            <h3 style={{ color: "var(--bs-dark)", marginTop: "10px" }}>
              Crate Scanned History
            </h3>
          </Col>
          <Col>
            <Button
              type="default"
              loading={isLoading}
              disabled={isLoading}
              onClick={() => {
                props.onCloseDrawer();
              }}
            >
              Close
            </Button>
          </Col>
        </Row>
      </FormHeader>
      <FormContent>
        <div
          style={{
            transition: "width 0.5s ease",
          }}
        >
          {isLoading ? (
            <Spin tip="Loading History..." />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                padding: "10px 0",
              }}
            >
              {prevScannedResult?.map((result, index) => (
                <Card
                  key={index}
                  size="small"
                  bodyStyle={{ padding: "1rem 1.25rem" }}
                  style={{
                    borderRadius: 6,
                    border: "1px solid #e5e5e5",
                    background: "#fff",
                    marginBottom: "1rem",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                  }}
                  hoverable
                >
                  <Row gutter={[12, 6]}>
                    {/* Serial Number */}
                    <Col span={24}>
                      <Space align="center">
                        <BarcodeOutlined
                          style={{ fontSize: 18, color: "#1677ff" }}
                        />
                        <Typography.Title
                          level={5}
                          style={{
                            margin: 0,
                            fontWeight: 500,
                            color: "#333",
                          }}
                        >
                          {result.serialNumber}
                        </Typography.Title>
                      </Space>
                    </Col>

                    {/* Crate Availability */}
                    <Col span={24}>
                      <Space size="small">
                        <BoxPlotOutlined style={{ color: "#595959" }} />
                        <Typography.Text style={{ fontSize: 14 }}>
                          {`Total ${result.blowerCount} out of ${result.crateCapacity} slots used`}
                        </Typography.Text>
                      </Space>
                    </Col>

                    {/* Optional: Progress Bar */}
                    <Col span={24}>
                      <div style={{ width: "100%", paddingRight: "1rem" }}>
                        <div
                          style={{
                            background: "#f0f0f0",
                            borderRadius: 4,
                            overflow: "hidden",
                            height: 8,
                          }}
                        >
                          <div
                            style={{
                              width: `${
                                (result.blowerCount / result.crateCapacity) *
                                100
                              }%`,
                              background: "#1677ff",
                              height: "100%",
                              transition: "width 0.3s ease",
                            }}
                          />
                        </div>
                      </div>
                    </Col>

                    {/* Scanned Date */}
                    <Col span={24}>
                      <Space size="small">
                        <CalendarOutlined style={{ color: "#8c8c8c" }} />
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 13 }}
                        >
                          Scanned on{" "}
                          {DateUtils.format(
                            result.scannedDate,
                            context?.user?.dateFormat,
                            true
                          )}
                        </Typography.Text>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              ))}
            </div>
          )}
        </div>
      </FormContent>
    </FormLayout>
  );
}

export default CrateScannedHistory;
